import React, { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Space, Table, Tag, Typography } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '@/utils/format';
import * as vatReturnService from '@/services/vatReturn.service';
import type { VatSettlementStatus } from '@/services/vatReturn.service';

const { Text } = Typography;

const money = (n: number) => formatMoney(n, { display: 'plain', locale: 'en-US' });
const date = (d: string | null) => (d && dayjs(d).isValid() ? dayjs(d).format('DD-MM-YYYY') : '');
const paymentColor: Record<string, string> = { Paid: 'green', 'Partially Paid': 'orange', Unpaid: 'red' };

/** FTA bill raised on posting and the payment vouchers allocated to it. */
const VatSettlementPanel: React.FC<{ returnId: string; refreshKey?: unknown }> = ({ returnId, refreshKey }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const [status, setStatus] = useState<VatSettlementStatus | null>(null);

  const paymentStatusLabel = (s: string) => {
    const map: Record<string, string> = {
      Paid: t('paymentStatusPaid'),
      'Partially Paid': t('paymentStatusPartiallyPaid'),
      Unpaid: t('paymentStatusUnpaid'),
    };
    return map[s] || s;
  };

  useEffect(() => {
    let cancelled = false;
    vatReturnService
      .getVatSettlementStatus(returnId)
      .then((s) => !cancelled && setStatus(s))
      .catch(() => !cancelled && setStatus(null));
    return () => {
      cancelled = true;
    };
  }, [returnId, refreshKey]);

  if (!status) return null;
  const { bill } = status;
  if (!bill) {
    return (
      <Alert
        type="info"
        showIcon
        message={
          status.journal_voucher
            ? t('settlementJournalPostedNoBill', { number: status.journal_voucher.voucher_number })
            : t('noSettlementRecorded')
        }
      />
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }} title={t('settlementWithFta')}>
        <Descriptions.Item label={t('ftaBill')}>
          <Link to={`/fin/direct-purchase-invoices/${bill.id}`}>{bill.invoice_number}</Link>
        </Descriptions.Item>
        <Descriptions.Item label={t('supplier')}>{bill.supplier_name}</Descriptions.Item>
        <Descriptions.Item label={t('due')}>{date(bill.due_date)}</Descriptions.Item>
        <Descriptions.Item label={t('amount')}>{money(bill.grand_total)}</Descriptions.Item>
        <Descriptions.Item label={t('paid')}>{money(bill.paid_amount)}</Descriptions.Item>
        <Descriptions.Item label={t('balanceDue')}>
          <Space>
            <Text strong>{money(bill.balance_due)}</Text>
            <Tag color={paymentColor[bill.payment_status] ?? 'default'}>{paymentStatusLabel(bill.payment_status)}</Tag>
          </Space>
        </Descriptions.Item>
        {status.journal_voucher && (
          <Descriptions.Item label={t('settlementJournal')}>{status.journal_voucher.voucher_number}</Descriptions.Item>
        )}
      </Descriptions>
      {bill.balance_due > 0.005 && (
        <Button type="primary" icon={<DollarOutlined />} onClick={() => navigate(`/fin/payment-vouchers/new?supplierInvoiceId=${encodeURIComponent(bill.id)}`)}>
          {t('payVatToFta')}
        </Button>
      )}
      {status.payments.length > 0 && (
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={status.payments}
          columns={[
            { title: t('paymentVoucherCol'), dataIndex: 'voucher_number', render: (v: string, r) => <Link to={`/fin/payment-vouchers/${r.id}`}>{v}</Link> },
            { title: t('date'), dataIndex: 'date', render: (d: string) => date(d) },
            { title: tc('status'), dataIndex: 'status', render: (s: string) => <Tag>{s}</Tag> },
            { title: t('amount'), dataIndex: 'amount', align: 'right', render: (n: number) => money(n) },
          ]}
        />
      )}
    </Space>
  );
};

export default VatSettlementPanel;
